// Iter-51 shared marker utilities.
// Three visual-only marker syntaxes ride along in reading text:
//   • `## `      — line prefix for a heading (bold + underlined + colon).
//   • `==word==` — inline highlight (yellow-highlighter background),
//                  MAY span multiple whitespace-separated words:
//                  `==water cycle==`, `==जल चक्र==`.
//   • `[[MATCH_EXERCISE]]{json}[[/MATCH_EXERCISE]]` — an entire
//                  "Match the following" / "मिलाइए" exercise, encoded
//                  as a single-line sentinel-wrapped JSON payload so
//                  the whole exercise stays as ONE chunk-indivisible
//                  unit. The `{json}` contains
//                  {instruction, left: [...], right: [...]}
//                  and NEVER contains the correct pairing (see M3 of
//                  the OCR prompt in server.py).
// Every UI surface that previews reading text should call stripMarkers()
// so a child (or parent) never sees `## …`, `==…==`, or the match
// sentinel printed raw.

export const HEADING_PREFIX = '## ';
export const HIGHLIGHT_RE = /==([^=\n]+)==/g;
export const MATCH_MARK_OPEN = '[[MATCH_EXERCISE]]';
export const MATCH_MARK_CLOSE = '[[/MATCH_EXERCISE]]';
export const MATCH_BLOCK_RE = /\[\[MATCH_EXERCISE\]\](\{[\s\S]*?\})\[\[\/MATCH_EXERCISE\]\]/;

// Numeric ("1. ") and alphabetic ("A. ") labels are applied purely for
// visual reading structure. They do NOT indicate the correct pairing.
function leftLabel(i) { return `${i + 1}.`; }
function rightLabel(i) {
  // A, B, C, … Z, then AA, AB… (only textbooks with > 26 items would ever
  // hit the second case; safety-net so a huge match doesn't crash).
  if (i < 26) return `${String.fromCharCode(65 + i)}.`;
  const first = String.fromCharCode(65 + Math.floor(i / 26) - 1);
  const second = String.fromCharCode(65 + (i % 26));
  return `${first}${second}.`;
}

/** Parse a `[[MATCH_EXERCISE]]{…json…}[[/MATCH_EXERCISE]]` sentinel out of
 * any text. Returns `{ isMatch: false }` when the text isn't a match block
 * or when the JSON is malformed / missing a required column. */
export function parseMatchExercise(text) {
  if (!text || typeof text !== 'string') return { isMatch: false };
  const m = MATCH_BLOCK_RE.exec(text);
  if (!m) return { isMatch: false };
  try {
    const payload = JSON.parse(m[1]);
    const instruction = String(payload.instruction || '').trim() || 'Match the following';
    const left = Array.isArray(payload.left) ? payload.left.map(x => String(x).trim()).filter(Boolean) : [];
    const right = Array.isArray(payload.right) ? payload.right.map(x => String(x).trim()).filter(Boolean) : [];
    if (!left.length || !right.length) return { isMatch: false };
    // Iter-52.11 — parallel array of cropped-picture URLs for the LEFT
    // column. Same length as `left`; empty string means "no crop for
    // this slot, fall back to the text descriptor". Absent field means
    // "no crops on this block at all" (text-only match, or legacy book
    // uploaded before the crop pipeline existed).
    const leftImagesRaw = Array.isArray(payload.left_images) ? payload.left_images : [];
    const leftImages = leftImagesRaw.length === left.length
      ? leftImagesRaw.map(x => String(x || '').trim())
      : new Array(left.length).fill('');
    return { isMatch: true, instruction, left, right, leftImages };
  } catch {
    return { isMatch: false };
  }
}

/** Flatten a match exercise into the readable form the child reads aloud
 * (instruction → Column A items → Column B items). This is what STT
 * scoring and TTS use so the mic + Read-to-me paths never see the
 * sentinel marker or the raw JSON. */
export function matchExerciseFlatText(match) {
  if (!match || !match.isMatch) return '';
  const left = match.left.map((v, i) => `${leftLabel(i)} ${v}.`).join(' ');
  const right = match.right.map((v, i) => `${rightLabel(i)} ${v}.`).join(' ');
  const instr = match.instruction.replace(/[.!?।:]+$/, '');
  return `${instr}. Column A: ${left} Column B: ${right}`;
}

/** Same accessors used by the renderer to display labeled items. */
export function matchLeftLabel(i) { return leftLabel(i); }
export function matchRightLabel(i) { return rightLabel(i); }

export function stripMarkers(text) {
  if (!text) return '';
  // Expand any match_exercise sentinel FIRST so downstream callers
  // (STT scoring, TTS `speak`, page previews) receive the flat readable
  // form instead of the raw JSON payload.
  let out = text.replace(MATCH_BLOCK_RE, (_, jsonBody) => {
    try {
      const payload = JSON.parse(jsonBody);
      const parsed = {
        isMatch: true,
        instruction: String(payload.instruction || '').trim() || 'Match the following',
        left: Array.isArray(payload.left) ? payload.left.map(x => String(x).trim()).filter(Boolean) : [],
        right: Array.isArray(payload.right) ? payload.right.map(x => String(x).trim()).filter(Boolean) : [],
      };
      return matchExerciseFlatText(parsed);
    } catch {
      // Malformed JSON — drop the sentinel so the child never sees raw
      // markers, but also don't fabricate content.
      return '';
    }
  });
  out = out
    .split('\n')
    .map(line => line.startsWith(HEADING_PREFIX) ? line.slice(HEADING_PREFIX.length) : line)
    .join('\n')
    .replace(HIGHLIGHT_RE, '$1');
  return out;
}

// Iter-52.55.4 — a "prefix marker" is a bullet glyph or a printed
// numbering/lettering label sitting at the START of a line, e.g.
//   `• The sky is blue.`
//   `1. The first people were nomadic.`
//   `(i) They hunted animals.`
//   `a) Wooden sticks`
//   `A. Cow`
//   `१. सूरज पूरब से उगता है।`
// These MUST appear on-screen (rule 4a/4b of the OCR prompt requires
// them so children can see the numbering), but must NOT be read aloud
// by Read-to-Me or scored by the mic. This regex targets ONLY the
// leading token; middle-of-sentence numbers ("There are 12 months")
// are preserved.
export const PREFIX_MARKER_RE = /^\s*(?:[•●○◦]\s+|\(?[0-9]{1,3}\)?[.)]\s+|\(?[a-zA-Z]\)?[.)]\s+|\((?:[ivxIVX]{1,4}|[a-zA-Z])\)\s+|[०-९]{1,3}[.)]\s+)/;

/** Strip visual-only prefix markers (bullets + printed labels) from
 *  each line while preserving everything else, INCLUDING the marker-
 *  expansion done by stripMarkers. Used by:
 *    • TTS speak(...)   — child shouldn't hear "bullet one".
 *    • STT scoring       — mic shouldn't fail the child for skipping
 *                          the "•" or the label.
 *  NEVER call this in a rendering path — the visible reading text
 *  MUST retain the bullet/label. */
export function stripForSpeech(text) {
  const expanded = stripMarkers(text);
  if (!expanded) return '';
  return expanded
    .split('\n')
    .map(line => line.replace(PREFIX_MARKER_RE, ''))
    .join('\n');
}

/** Returns true if a single word token (after whitespace split) is a
 *  bullet-glyph-only token OR a printed label token that would be
 *  stripped by stripForSpeech when it appears at line-start.
 *  Used by the Reading page's word-token renderer to skip the token
 *  for karaoke wordIdx counting + missed-word status assignment so
 *  the mic scoring stays aligned with what's actually spoken. */
export function isNonSpeechPrefixToken(word) {
  if (!word) return false;
  return /^(?:[•●○◦]|\(?[0-9]{1,3}\)?[.)]|\(?[a-zA-Z]\)?[.)]|\((?:[ivxIVX]{1,4}|[a-zA-Z])\)|[०-९]{1,3}[.)])$/.test(word);
}

// Iter-52.55.5 — a Match-the-Following LEFT-column item is a PICTURE
// item when either (a) the OCR match_exercise block supplied a crop
// URL in `left_images[i]`, or (b) the item's TEXT starts with one of
// the OCR-emitted picture prefixes ('picture', 'photo', 'image' /
// Hindi 'चित्र', 'फोटो', 'तस्वीर' — see rule M4 of the OCR prompt).
// The frontend uses this to HIDE the AI description text (it leaks
// the answer) and show only the cropped image.
export function isPictureItem(text, imageUrl) {
  if (imageUrl && String(imageUrl).trim()) return true;
  const t = String(text || '').trim().toLowerCase();
  if (!t) return false;
  return /^(?:picture\b|photo\b|image\b|illustration\b|चित्र|फोटो|तस्वीर)/i.test(t);
}
